/**
 * TaskAttachmentsSection — the files on one task (TASK-UX1).
 *
 * A sidebar card, so it stays visible next to the discussion instead of
 * being buried under it. Three things happen here and nothing else:
 * upload one file, open one, delete one.
 *
 * Why a download is TWO requests: the backend never puts the R2 object
 * key on the wire (``task_attachment_service`` module docstring). The
 * browser asks for a signed URL, valid for minutes, and follows it. So
 * there is no href to render up front — the link has to be a button that
 * fetches, and a failure has to be visible rather than a dead click.
 *
 * The type and size hints below MIRROR the server's limits
 * (``ALLOWED_MIME_TYPES`` / ``MAX_FILE_SIZE_BYTES``); they are a
 * courtesy, never the check. The server rejects with a German detail and
 * that message is shown verbatim — inventing a local reason would
 * eventually contradict the real one.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, Paperclip, Trash2, Upload } from "lucide-react";

import {
  deleteAdminTaskAttachment,
  fetchAdminTaskAttachmentDownloadUrl,
  fetchAdminTaskAttachments,
  uploadAdminTaskAttachment,
} from "../../../api/adminData";
import { formatDateTime } from "../../../utils/datetime";

/** Mirrors the server's 25 MB ceiling — shown, never enforced here. */
export const MAX_ATTACHMENT_MB = 25;

/**
 * What the file dialog offers. A superset filter, not a guarantee: a
 * user can always pick "all files", which is exactly why the server
 * validates the declared type as well as the extension.
 */
export const ACCEPTED_FILE_TYPES = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".heic",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
  ".txt",
  ".zip",
].join(",");

/**
 * "412 KB", "1.8 MB" — never raw bytes, which nobody reads. Rounds to
 * one decimal from MB upwards; below a kilobyte it says so honestly
 * instead of showing "0.0 KB".
 */
export function formatFileSize(bytes) {
  // `Number(null)` is 0, so a missing size would otherwise read as an
  // empty file — a claim, where a dash is an honest absence.
  if (bytes == null || bytes === "") return "—";
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function TaskAttachmentsSection({
  taskId,
  onChanged,
  /** REPORT-ATTACH1: the attachment ENGINE is entity-agnostic — an
   * ``api`` override ({list, upload(file), downloadUrl(id),
   * remove(id)}) rebinds it to another parent
   * (WorkReportAttachmentsSection does). Without it the task API
   * applies, byte-for-byte as before. */
  api = null,
  /** Frozen parent (cancelled report): list + download stay open,
   * upload and delete disappear, the hint explains why. Backend
   * enforces the same freeze with 409s. */
  readOnly = false,
  readOnlyHint = null,
  headingId = "task-attachments-heading",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const fileInputRef = useRef(null);

  const reload = useCallback(async () => {
    if (!taskId) {
      setItems([]);
      setLoading(false);
      return;
    }
    try {
      const rows = await (api?.list ? api.list() : fetchAdminTaskAttachments(taskId));
      setItems(Array.isArray(rows) ? rows : []);
      setError("");
    } catch (err) {
      setError(err?.message || "Anhänge konnten nicht geladen werden.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [taskId, api]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      if (!taskId) {
        if (!cancelled) {
          setItems([]);
          setLoading(false);
        }
        return;
      }
      try {
        const rows = await (api?.list ? api.list() : fetchAdminTaskAttachments(taskId));
        if (cancelled) return;
        setItems(Array.isArray(rows) ? rows : []);
        setError("");
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Anhänge konnten nicht geladen werden.");
        setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [taskId, api]);

  async function handleSelected(event) {
    const file = event.target.files?.[0];
    // Reset first: picking the SAME file twice must fire change again,
    // which it will not while the input still holds it.
    event.target.value = "";
    if (!file || !taskId) return;
    setUploading(true);
    setError("");
    try {
      await (api?.upload
        ? api.upload(file)
        : uploadAdminTaskAttachment(taskId, file));
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err?.message || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  }

  async function handleOpen(attachment) {
    setBusyId(attachment.id);
    setError("");
    try {
      const data = await (api?.downloadUrl
        ? api.downloadUrl(attachment.id)
        : fetchAdminTaskAttachmentDownloadUrl(taskId, attachment.id));
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        setError("Download konnte nicht gestartet werden.");
      }
    } catch (err) {
      setError(err?.message || "Download konnte nicht gestartet werden.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(attachment) {
    // Deleting a file removes the binary too — the confirmation names
    // the file so a mis-click on a crowded list is recoverable.
    if (
      !window.confirm(`Anhang „${attachment.file_name}" wirklich löschen?`)
    ) {
      return;
    }
    setBusyId(attachment.id);
    setError("");
    try {
      await (api?.remove
        ? api.remove(attachment.id)
        : deleteAdminTaskAttachment(taskId, attachment.id));
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err?.message || "Löschen fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-[10px] border border-border bg-card p-4 dark:border-[#1e2130] dark:bg-[#13161f]"
    >
      <header className="mb-3 flex items-center justify-between gap-3">
        <h2
          id={headingId}
          className="m-0 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.09em] text-muted-foreground dark:text-[#4a5070]"
        >
          <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
          Anhänge
          {items.length > 0 ? (
            <span className="tabular-nums font-normal">({items.length})</span>
          ) : null}
        </h2>
      </header>

      {readOnly ? (
        readOnlyHint ? (
          <p className="m-0 mb-2 text-[11px] text-muted-foreground dark:text-[#4a5070]">
            {readOnlyHint}
          </p>
        ) : null
      ) : (
      <>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={handleSelected}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading || !taskId}
        className="flex min-h-[40px] w-full items-center justify-center gap-2 rounded-[8px] border border-dashed border-border bg-transparent px-3 py-2 text-[12px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#1e2130] dark:text-[#94a3b8] dark:hover:bg-[#0d0f14]"
      >
        <Upload className="h-3.5 w-3.5" aria-hidden="true" />
        {uploading ? "Wird hochgeladen …" : "Datei hochladen"}
      </button>
      <p className="mt-2 mb-0 text-[11px] text-muted-foreground dark:text-[#4a5070]">
        PDF, Bilder, Office-Dateien, CSV, TXT oder ZIP — max{" "}
        {MAX_ATTACHMENT_MB} MB.
      </p>
      </>
      )}

      {error ? (
        <p
          role="alert"
          className="mt-2 mb-0 text-[12px] font-medium text-destructive dark:text-[#ff6b6b]"
        >
          {error}
        </p>
      ) : null}

      <div className="mt-3">
        {loading ? (
          <p
            role="status"
            className="m-0 text-[12px] text-muted-foreground dark:text-[#4a5070]"
          >
            Lade Anhänge …
          </p>
        ) : items.length === 0 ? (
          <p className="m-0 text-[12px] text-muted-foreground dark:text-[#4a5070]">
            Noch keine Anhänge.
          </p>
        ) : (
          <ul className="m-0 grid list-none gap-2 p-0">
            {items.map((a) => (
              <li
                key={String(a.id)}
                className="rounded-[8px] border border-border bg-background px-3 py-2 dark:border-[#1e2130] dark:bg-[#0d0f14]"
              >
                <p
                  className="m-0 break-words text-[12px] font-semibold text-foreground dark:text-[#c5cbe0]"
                  title={a.file_name}
                >
                  {a.file_name}
                </p>
                <p className="m-0 mt-0.5 text-[11px] text-muted-foreground dark:text-[#4a5070]">
                  <span className="tabular-nums">
                    {formatFileSize(a.size_bytes)}
                  </span>
                  {" · "}
                  <span className="tabular-nums">
                    {formatDateTime(a.created_at)}
                  </span>
                  {a.uploaded_by_name ? ` · ${a.uploaded_by_name}` : ""}
                </p>
                <div className="mt-1.5 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleOpen(a)}
                    disabled={busyId === a.id}
                    aria-label={`${a.file_name} herunterladen`}
                    className="inline-flex min-h-[32px] items-center gap-1 rounded-[6px] border border-border bg-transparent px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#1e2130] dark:text-[#94a3b8] dark:hover:bg-[#13161f]"
                  >
                    <Download className="h-3 w-3" aria-hidden="true" />
                    Öffnen
                  </button>
                  {readOnly ? null : (
                  <button
                    type="button"
                    onClick={() => handleDelete(a)}
                    disabled={busyId === a.id}
                    aria-label={`${a.file_name} löschen`}
                    className="inline-flex min-h-[32px] items-center gap-1 rounded-[6px] border border-border bg-transparent px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#1e2130] dark:text-[#94a3b8] dark:hover:text-[#ff6b6b]"
                  >
                    <Trash2 className="h-3 w-3" aria-hidden="true" />
                    Löschen
                  </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
